import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { getScoreColor, getScoreLabel, channelIcons } from '../data/mockData';
import GlobalFilters from '../components/GlobalFilters';
import { useFilter } from '../context/FilterContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiTrendingUp, FiTrendingDown, FiMinus, FiExternalLink } = FiIcons;

const API_URL = 'http://localhost:3001';

const MediaDetail = () => {
  const { mediaId } = useParams();
  const navigate = useNavigate();
  const { filters } = useFilter();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch media data from API
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_URL}/api/media/${mediaId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setMedia(data);
      } catch (err) {
        console.error('Error fetching media:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [mediaId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่พบข้อมูลสื่อโฆษณา</h2>
          <p className="text-gray-500 mb-4">สื่อโฆษณาที่คุณค้นหาไม่มีในระบบ (ID: {mediaId})</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  // Filter channels based on current channel filters
  const availableChannels = Object.entries(media.channels).filter(([channelId]) => {
    if (filters.channels.length === 0) return true;
    return filters.channels.includes(channelId);
  });

  const channels = Object.values(media.channels);
  const bestRoas = Math.max(...channels.map(c => c.roas));
  const bestCpa = Math.min(...channels.map(c => c.cpa));
  const bestCtr = Math.max(...channels.map(c => c.ctr));

  const getTrendIcon = (value, best, isLowerBetter = false) => {
    if (value === best) return FiMinus;
    if (isLowerBetter) {
      return value < best ? FiTrendingUp : FiTrendingDown;
    }
    return value > best ? FiTrendingUp : FiTrendingDown;
  };

  const getTrendColor = (value, best, isLowerBetter = false) => {
    if (value === best) return 'text-gray-500';
    if (isLowerBetter) {
      return value < best ? 'text-green-500' : 'text-red-500';
    }
    return value > best ? 'text-green-500' : 'text-red-500';
  };

  const handleChannelClick = (channelId) => {
    navigate(`/media/${mediaId}/channel/${channelId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5 text-gray-600" />
            </motion.button>
            <div className="flex items-center space-x-4">
              <img 
                src={media.thumbnail} 
                alt={media.name} 
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{media.name}</h1>
                <p className="text-gray-500">{media.product}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Global Filters - No performance filter */}
        <GlobalFilters 
          showPerformanceFilter={false} 
          showSearchFilter={false} 
          showChannelFilter={true}
          showDateFilter={false}
        />

        {/* Filter Results Info */}
        {filters.channels.length > 0 && (
          <motion.div
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-blue-800">
              <span className="font-medium">กรองช่องทาง:</span> แสดง {availableChannels.length} จาก {Object.keys(media.channels).length} ช่องทาง
            </div>
          </motion.div>
        )}

        {/* Overall Stats */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {(Object.values(media.channels).reduce((sum, ch) => sum + ch.revenue, 0) / 
                Object.values(media.channels).reduce((sum, ch) => sum + ch.spent, 0)).toFixed(1)}x
            </div>
            <div className="text-sm text-gray-500">ROAS รวม</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">
              ฿{Object.values(media.channels).reduce((sum, ch) => sum + ch.spent, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">งบประมาณรวม</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">
              {Object.values(media.channels).reduce((sum, ch) => sum + ch.conversions, 0)}
            </div>
            <div className="text-sm text-gray-500">Conversions รวม</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">
              ฿{Object.values(media.channels).reduce((sum, ch) => sum + ch.revenue, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">รายได้รวม</div>
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">เปรียบเทียบประสิทธิภาพรายช่องทาง</h2>
            <p className="text-sm text-gray-500 mt-1">คลิกที่แถวเพื่อดูรายละเอียดเชิงลึกของแต่ละช่องทาง</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ช่องทาง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    คะแนน
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROAS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CPA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CTR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    งบประมาณ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    การดำเนินการ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {availableChannels.map(([channelId, channel], index) => (
                  <motion.tr
                    key={channelId}
                    className="hover:bg-blue-50 transition-all cursor-pointer group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => handleChannelClick(channelId)}
                    whileHover={{ scale: 1.005 }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{channelIcons[channelId]}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{channel.name}</span>
                          <SafeIcon 
                            icon={FiExternalLink} 
                            className="h-3 w-3 text-gray-400 group-hover:text-blue-500 transition-colors opacity-0 group-hover:opacity-100" 
                          />
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${getScoreColor(channel.score)}`}>
                          {channel.score}
                        </span>
                        <span className="text-xs text-gray-500">({getScoreLabel(channel.score)})</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className={`text-sm font-medium ${channel.roas === bestRoas ? 'text-green-600' : 'text-gray-900'}`}>
                          {channel.roas}x
                        </span>
                        <SafeIcon 
                          icon={getTrendIcon(channel.roas, bestRoas)} 
                          className={`h-4 w-4 ${getTrendColor(channel.roas, bestRoas)}`} 
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className={`text-sm font-medium ${channel.cpa === bestCpa ? 'text-green-600' : 'text-gray-900'}`}>
                          ฿{channel.cpa.toLocaleString()}
                        </span>
                        <SafeIcon 
                          icon={getTrendIcon(channel.cpa, bestCpa, true)} 
                          className={`h-4 w-4 ${getTrendColor(channel.cpa, bestCpa, true)}`} 
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className={`text-sm font-medium ${channel.ctr === bestCtr ? 'text-green-600' : 'text-gray-900'}`}>
                          {channel.ctr}%
                        </span>
                        <SafeIcon 
                          icon={getTrendIcon(channel.ctr, bestCtr)} 
                          className={`h-4 w-4 ${getTrendColor(channel.ctr, bestCtr)}`} 
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ฿{channel.spent.toLocaleString()}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {channel.conversions}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChannelClick(channelId);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span>ดูรายละเอียดเชิงลึก</span>
                        <SafeIcon icon={FiExternalLink} className="h-3 w-3" />
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>

            {availableChannels.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">ไม่พบช่องทางที่ตรงกับตัวกรอง</div>
              </div>
            )}
          </div>

          {/* Quick Action Hint */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-center text-sm text-gray-500">
              <SafeIcon icon={FiExternalLink} className="h-4 w-4 mr-2" />
              คลิกที่แถวใดก็ได้เพื่อดูข้อมูลเชิงลึกของช่องทางนั้น ๆ
            </div>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ช่องทางที่ดีที่สุด</h3>
            <div className="space-y-3">
              {availableChannels
                .sort(([, a], [, b]) => b.score - a.score)
                .slice(0, 3)
                .map(([channelId, channel]) => (
                  <div
                    key={channelId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                    onClick={() => handleChannelClick(channelId)}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{channelIcons[channelId]}</span>
                      <span className="text-sm font-medium text-gray-900">{channel.name}</span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${getScoreColor(channel.score)}`}>
                      {channel.score}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ROAS สูงสุด</h3>
            <div className="space-y-3">
              {availableChannels
                .sort(([, a], [, b]) => b.roas - a.roas)
                .slice(0, 3)
                .map(([channelId, channel]) => (
                  <div
                    key={channelId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                    onClick={() => handleChannelClick(channelId)}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{channelIcons[channelId]}</span>
                      <span className="text-sm font-medium text-gray-900">{channel.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-green-600">{channel.roas}x</span>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">CPA ต่ำสุด</h3>
            <div className="space-y-3">
              {availableChannels
                .sort(([, a], [, b]) => a.cpa - b.cpa)
                .slice(0, 3)
                .map(([channelId, channel]) => (
                  <div
                    key={channelId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                    onClick={() => handleChannelClick(channelId)}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{channelIcons[channelId]}</span>
                      <span className="text-sm font-medium text-gray-900">{channel.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">฿{channel.cpa}</span>
                  </div>
                ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default MediaDetail;
